import { omit } from 'lodash-es';
import {
  addAnswer,
  setCurrentStepIndex,
  setLoading,
  updateActiveCategory,
  addUniqIncentivesIds,
  removeIncentivesIds,
  updateIncentives,
  updateMeta,
  rememberNewFlowIncentives,
  removeAnswer,
} from 'site-modules/shared/components/incentives/incentives-wizard/state-manager/actions';
import {
  CHECK_FEDERAL_REBATES_SCREEN_ID,
  CHECK_LOCAL_REBATES_SCREEN_ID,
  INSTALL_EV_CHARGER_QUESTION_ID,
  LOWER_INCOME_YEAR_QUESTION_ID,
  TRANSACTION_TYPE_QUESTION_ID,
  ZIP_CODE_QUESTION_ID,
  questions,
  CHECKING_FEDERAL_ELIGIBILITY_RESULT_SCREEN_ID,
  CHECKING_LOCAL_ELIGIBILITY_RESULT_SCREEN_ID,
  TAX_FILING_STATUS_FEDERAL_QUESTION_ID,
  ADJUSTED_GROSS_INCOME_LOCAL_QUESTION_ID,
  ADJUSTED_GROSS_INCOME_FEDERAL_QUESTION_ID,
  TAX_FILING_STATUS_LOCAL_QUESTION_ID,
  ENGINE_TYPE_QUESTION_ID,
} from 'site-modules/shared/components/incentives/incentives-wizard/questions/questions';
import { ELIGIBILITY } from 'site-modules/shared/components/incentives/incentives-wizard/constants/screen-types';
import { QUESTION } from 'site-modules/shared/components/incentives/incentives-wizard/constants/question-variants';
import { flowBuilder } from 'site-modules/shared/components/incentives/incentives-wizard/flow-builders/flow-builder';
import { isSameAnswer } from 'site-modules/shared/components/incentives/incentives-wizard/utils/answer-status';
import { createEligibilityFactorFilter } from 'site-modules/shared/components/incentives/incentives-wizard/utils/create-eligibility-factor-filter';
import { checkIncentiveBelongsToCategory } from 'site-modules/shared/components/incentives/incentives-wizard/utils/check-incentive-belongs-to-category';
import { getIncentivesWithMaxRebate } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-incentives-with-max-rebate';
import { getCurrentYear } from 'site-modules/shared/utils/time-util';
import { getTaxFilingStatuses } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-tax-filing-statuses';
import { filterOutIncentivesWithClientSideEligibilityCheck } from 'site-modules/shared/components/incentives/incentives-wizard/utils/filter-out-incentives-with-client-side-eligibility-check';

function basicStepProcessor({ state, dispatch }) {
  if (state.flowSteps.at(state.currentStepIndex + 1)) {
    dispatch(setCurrentStepIndex(state.currentStepIndex + 1));
  }
}

function zipCodeTypeQuestionProcessor({ state, dispatch, answer }) {
  dispatch(updateMeta({ zip: answer }));
  basicStepProcessor({ state, dispatch });
}

function transactionTypeQuestionProcessor({ state, dispatch, onFilterIncentives, answer }) {
  const { flowSteps, currentStepIndex, answers, stepMeta } = state;
  const {
    zip: [zip, { isChanged }],
    engineTypes,
  } = stepMeta;
  const sameAnswer = isSameAnswer(flowSteps.at(currentStepIndex), answers, answer);
  if (sameAnswer && !isChanged) {
    basicStepProcessor({ state, dispatch });
    return;
  }
  dispatch(setLoading(true));
  onFilterIncentives({
    incentiveFilters: { transactionType: answer[0].toUpperCase(), zip, ...(engineTypes ? { engineTypes } : {}) },
  });
}

const updateAnswerOnSameLocalQuestion =
  (processor, localQuestionId) =>
  ({ state, dispatch, answer, incentives }) => {
    const lowerIncomeYearAnswer = state.answers[LOWER_INCOME_YEAR_QUESTION_ID];

    if (!lowerIncomeYearAnswer) {
      return processor({ state, dispatch, answer, incentives });
    }

    if (Number(lowerIncomeYearAnswer) === getCurrentYear() - 1) {
      dispatch(addAnswer(localQuestionId, answer));
      return processor({
        state: { ...state, answers: { ...state.answers, [localQuestionId]: answer } },
        dispatch,
        answer,
        incentives,
      });
    }

    if (state.answers[localQuestionId]) {
      dispatch(removeAnswer(localQuestionId));
      return processor({
        state: { ...state, answers: omit(state.answers, [localQuestionId]) },
        dispatch,
        answer,
        incentives,
      });
    }

    return processor({ state, dispatch, answer, incentives });
  };

function lowerIncomeTypeQuestionProcessor({ state, dispatch, answer, context }) {
  const { currentStepIndex, flowSteps, flowIncentives } = state;
  flowBuilder.buildFlow(
    { ...state, answers: { ...state.answers, [flowSteps.at(currentStepIndex)]: answer } },
    dispatch,
    flowIncentives,
    context
  );
  dispatch(updateMeta({ lowerIncomeYear: answer[0] }));
  basicStepProcessor({ state, dispatch });
}

function taxFillingStatusTypeQuestionProcessor({ state, dispatch }) {
  const { activeCategory, flowIncentives } = state;
  const taxFilingStatuses = getTaxFilingStatuses(flowIncentives, activeCategory);
  dispatch(updateMeta({ taxFilingStatuses }));
  basicStepProcessor({ state, dispatch });
}

function installEvChargerTypeQuestionProcessor({ state, dispatch, answer, context }) {
  const { currentStepIndex, flowSteps, flowIncentives } = state;

  flowBuilder.buildFlow(
    { ...state, answers: { ...state.answers, [flowSteps.at(currentStepIndex)]: answer } },
    dispatch,
    flowIncentives,
    context
  );

  basicStepProcessor({ state, dispatch });
}

/**
 * CHECKING_LOCAL_ELIGIBILITY_RESULT_SCREEN_ID question processor.
 *
 * @param {Object} options
 * @param {WizardState} options.state - wizard internal state
 * @param {Function} options.dispatch - wizard state update function
 * @param {Function} options.onFilterIncentives - function to update wizard model
 * @description
 * This is a specific screen in the flow. It is added at the end of each category
 * if there were questions for which the API checks for eligibility.
 */
function checkingEligibilityTypeScreenProcessor({ state, dispatch, onFilterIncentives }) {
  dispatch(setLoading(true));
  const eligibilityFactors = createEligibilityFactorFilter(state);
  onFilterIncentives({ eligibilityFactors });
}

function checkRebatesTypeScreenProcessor({ state, dispatch, answer }) {
  dispatch(updateActiveCategory(answer[0]));
  basicStepProcessor({ state, dispatch });
}

function engineTypeQuestionProcessor({ state, dispatch, answer }) {
  dispatch(updateMeta({ engineTypes: answer }));
  basicStepProcessor({ state, dispatch });
}

const stepProcessorMap = {
  [ZIP_CODE_QUESTION_ID]: zipCodeTypeQuestionProcessor,
  [TRANSACTION_TYPE_QUESTION_ID]: transactionTypeQuestionProcessor,
  [LOWER_INCOME_YEAR_QUESTION_ID]: lowerIncomeTypeQuestionProcessor,
  [ADJUSTED_GROSS_INCOME_FEDERAL_QUESTION_ID]: updateAnswerOnSameLocalQuestion(
    basicStepProcessor,
    ADJUSTED_GROSS_INCOME_LOCAL_QUESTION_ID
  ),
  [TAX_FILING_STATUS_FEDERAL_QUESTION_ID]: updateAnswerOnSameLocalQuestion(
    taxFillingStatusTypeQuestionProcessor,
    TAX_FILING_STATUS_LOCAL_QUESTION_ID
  ),
  [TAX_FILING_STATUS_LOCAL_QUESTION_ID]: taxFillingStatusTypeQuestionProcessor,
  [INSTALL_EV_CHARGER_QUESTION_ID]: installEvChargerTypeQuestionProcessor,
  [CHECK_FEDERAL_REBATES_SCREEN_ID]: checkRebatesTypeScreenProcessor,
  [CHECK_LOCAL_REBATES_SCREEN_ID]: checkRebatesTypeScreenProcessor,
  [CHECKING_FEDERAL_ELIGIBILITY_RESULT_SCREEN_ID]: checkingEligibilityTypeScreenProcessor,
  [CHECKING_LOCAL_ELIGIBILITY_RESULT_SCREEN_ID]: checkingEligibilityTypeScreenProcessor,
  [ENGINE_TYPE_QUESTION_ID]: engineTypeQuestionProcessor,
};

/**
 * Processes answers to questions.
 *
 * @param {Object} options
 * @param {WizardState} options.state - wizard internal state
 * @param {Function} options.dispatch - wizard state update function
 * @param {Function} options.onFilterIncentives - function to update wizard model
 * @param {Array} options.answer - answer to the current question
 * @param {Array} options.incentives - flowIncentives
 * @param {Object} options.context - general external information required by the wizard
 * @description
 * Adds a question answer to the state and calls the specific question processor or the base one, which switches flow to next question
 */
function processAnswer({ state, dispatch, onFilterIncentives, answer, incentives, context }) {
  const { currentStepIndex, flowSteps } = state;
  const currentStepId = flowSteps.at(currentStepIndex);
  dispatch(addAnswer(currentStepId, answer));

  const stepSpecificProcessor = stepProcessorMap[currentStepId] || basicStepProcessor;
  stepSpecificProcessor({ state, dispatch, onFilterIncentives, answer, incentives, context });
}

function basicPostIncentivesProcessor({ dispatch, incentives }) {
  dispatch(setLoading(false));
  dispatch(updateIncentives(incentives));
}

/**
 * Checks if the user is eligible for incentives for the current category.
 *
 * @param {Object} options
 * @param {WizardState} options.state - wizard internal state
 * @param {Function} options.dispatch - wizard state update function
 * @param {Array} options.incentives - flowIncentives
 */
function eligibilityCheckerProcessor({ state, dispatch, incentives }) {
  const { activeCategory, incentives: allIncentives, answers } = state;

  const incentivesForCurrentCategory = filterOutIncentivesWithClientSideEligibilityCheck(
    incentives.filter(incentive => checkIncentiveBelongsToCategory(activeCategory, incentive)),
    answers
  );

  basicPostIncentivesProcessor({ dispatch, incentives: incentivesForCurrentCategory });

  const eligibleIncentivesIds = getIncentivesWithMaxRebate(incentivesForCurrentCategory).map(({ id }) => id);

  const savedIncentivesIdsForCurrentCategory = allIncentives
    .filter(incentive => checkIncentiveBelongsToCategory(activeCategory, incentive))
    .map(({ id }) => id);

  dispatch(removeIncentivesIds(savedIncentivesIdsForCurrentCategory));
  dispatch(addUniqIncentivesIds(eligibleIncentivesIds));

  basicStepProcessor({ state, dispatch });
}

function postIncentivesUpdateProcessor({ state, dispatch, incentives, context }) {
  basicPostIncentivesProcessor({ dispatch, incentives });
  flowBuilder.buildFlow(state, dispatch, incentives, context);

  dispatch(rememberNewFlowIncentives(incentives));
  basicStepProcessor({ state, dispatch });
}

const stepPostModelUpdateProcessorMap = {
  [TRANSACTION_TYPE_QUESTION_ID]: postIncentivesUpdateProcessor,
};

/**
 * Postprocessor for handling asynchronous responses to incentives queries that were executed by a specific question processor.
 *
 * @param {Object} options
 * @param {WizardState} options.state - wizard internal state
 * @param {Function} options.dispatch - wizard state update function
 * @param {Array} options.incentives - flowIncentives
 * @param {Object} options.context - general external information required by the wizard
 */
async function processIncentivesUpdate({ state, dispatch, incentives, context }) {
  const currentStepId = state.flowSteps.at(state.currentStepIndex);
  const postModelUpdateProcess = stepPostModelUpdateProcessorMap[currentStepId] || eligibilityCheckerProcessor;
  postModelUpdateProcess({ state, dispatch, incentives, context });
}

function processBack({ state, dispatch }) {
  const { flowSteps, currentStepIndex, activeCategory } = state;
  const prevQuestionIndex = flowSteps
    .slice(0, currentStepIndex)
    .findLastIndex(stepId => questions[stepId].variant === QUESTION);
  const prevQuestionCategory =
    questions[
      flowSteps[flowSteps.slice(0, prevQuestionIndex).findLastIndex(stepId => questions[stepId].type === ELIGIBILITY)]
    ].categories[0];
  if (prevQuestionCategory !== activeCategory) {
    dispatch(updateActiveCategory(prevQuestionCategory));
  }

  dispatch(setCurrentStepIndex(prevQuestionIndex));
}

export const flowProcessor = {
  processIncentivesUpdate,
  processAnswer,
  processBack,
};
